<template>
  <div class="audio-controls">
    <!-- 基础调节 -->
    <div class="control-section">
      <h3 class="section-title">基础增益</h3>
      <div class="control-row">
        <div class="control-group" :class="{ disabled: !params.volumeEnabled }">
          <div class="label-row">
            <label class="cyber-switch">
              <input
                type="checkbox"
                :checked="params.volumeEnabled"
                @change="$emit('update:params', { ...params, volumeEnabled: $event.target.checked })"
              >
              <span class="switch-slider"></span>
            </label>
            <label class="control-label">实时音量 (Gain)</label>
          </div>
          <div class="slider-wrap">
            <input
              type="range" min="0" max="2" step="0.01"
              :value="params.volume"
              :disabled="!params.volumeEnabled"
              @input="$emit('update:params', { ...params, volume: parseFloat($event.target.value), volumeEnabled: true })"
              class="cyber-slider"
            >
            <span class="control-value">{{ Math.round(params.volume * 100) }}%</span>
          </div>
        </div>
        <div class="control-group" :class="{ disabled: !params.playbackRateEnabled }">
          <div class="label-row">
            <label class="cyber-switch">
              <input
                type="checkbox"
                :checked="params.playbackRateEnabled"
                @change="$emit('update:params', { ...params, playbackRateEnabled: $event.target.checked })"
              >
              <span class="switch-slider"></span>
            </label>
            <label class="control-label">播放速度 (Rate)</label>
          </div>
          <div class="slider-wrap">
            <input
              type="range" min="0.8" max="1.2" step="0.01"
              :value="params.playbackRate"
              :disabled="!params.playbackRateEnabled"
              @input="$emit('update:params', { ...params, playbackRate: parseFloat($event.target.value), playbackRateEnabled: true })"
              class="cyber-slider"
            >
            <span class="control-value">{{ params.playbackRate.toFixed(2) }}x</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 深度去重核心 (针对短视频审核) -->
    <div class="control-section">
      <h3 class="section-title">去重核心 (算法级)</h3>
      <div class="control-row">
        <div class="control-group" :class="{ disabled: !params.pitchEnabled }">
          <div class="label-row">
            <label class="cyber-switch">
              <input
                type="checkbox"
                :checked="params.pitchEnabled"
                @change="$emit('update:params', { ...params, pitchEnabled: $event.target.checked })"
              >
              <span class="switch-slider"></span>
            </label>
            <label class="control-label">音调偏移 (Pitch)</label>
          </div>
          <div class="slider-wrap">
            <input
              type="range" min="-1200" max="1200" step="10"
              :value="params.pitch"
              :disabled="!params.pitchEnabled"
              @input="$emit('update:params', { ...params, pitch: parseInt($event.target.value), pitchEnabled: true })"
              class="cyber-slider"
            >
            <span class="control-value">{{ params.pitch > 0 ? '+' : '' }}{{ params.pitch }}ct</span>
          </div>
        </div>

        <div class="control-group" :class="{ disabled: !params.compressorEnabled }">
          <div class="label-row">
            <label class="cyber-switch">
              <input
                type="checkbox"
                :checked="params.compressorEnabled"
                @change="$emit('update:params', { ...params, compressorEnabled: $event.target.checked })"
              >
              <span class="switch-slider"></span>
            </label>
            <label class="control-label">强压缩器 (DYN)</label>
          </div>
          <div class="slider-wrap">
            <input
              type="range" min="-100" max="0" step="1"
              :value="params.compressorThreshold"
              :disabled="!params.compressorEnabled"
              @input="$emit('update:params', { ...params, compressorThreshold: parseInt($event.target.value), compressorEnabled: true })"
              class="cyber-slider"
            >
            <span class="control-value">{{ Math.round(params.compressorThreshold) }}dB</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 环境与空间 -->
    <div class="control-section">
      <h3 class="section-title">声场重塑</h3>
      <div class="control-row">
        <div class="control-group" :class="{ disabled: !params.reverbEnabled }">
          <div class="label-row">
            <label class="cyber-switch">
              <input
                type="checkbox"
                :checked="params.reverbEnabled"
                @change="$emit('update:params', { ...params, reverbEnabled: $event.target.checked })"
              >
              <span class="switch-slider"></span>
            </label>
            <label class="control-label">混响密度 (RVB)</label>
          </div>
          <div class="slider-wrap">
            <input
              type="range" min="0" max="1" step="0.01"
              :value="params.reverb"
              :disabled="!params.reverbEnabled"
              @input="$emit('update:params', { ...params, reverb: parseFloat($event.target.value), reverbEnabled: true })"
              class="cyber-slider"
            >
            <span class="control-value">{{ Math.round(params.reverb * 100) }}%</span>
          </div>
        </div>

        <div class="control-group" :class="{ disabled: !params.bassEnabled }">
          <div class="label-row">
            <label class="cyber-switch">
              <input
                type="checkbox"
                :checked="params.bassEnabled"
                @change="$emit('update:params', { ...params, bassEnabled: $event.target.checked })"
              >
              <span class="switch-slider"></span>
            </label>
            <label class="control-label">低音增强 (BASS)</label>
          </div>
          <div class="slider-wrap">
            <input
              type="range" min="0" max="20" step="1"
              :value="params.bass"
              :disabled="!params.bassEnabled"
              @input="$emit('update:params', { ...params, bass: parseFloat($event.target.value), bassEnabled: true })"
              class="cyber-slider"
            >
            <span class="control-value">{{ params.bass.toFixed(1) }}dB</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 干扰注入 -->
    <div class="control-section">
      <h3 class="section-title">抗封锁注入</h3>
      <div class="control-row">
        <div class="control-group" :class="{ disabled: !params.vocalClarityEnabled }">
          <div class="label-row">
            <label class="cyber-switch">
              <input
                type="checkbox"
                :checked="params.vocalClarityEnabled"
                @change="$emit('update:params', { ...params, vocalClarityEnabled: $event.target.checked })"
              >
              <span class="switch-slider"></span>
            </label>
            <label class="control-label">人声清晰度增强</label>
          </div>
          <div class="slider-wrap">
            <input
              type="range" min="0" max="10" step="0.1"
              :value="params.vocalClarityLevel"
              :disabled="!params.vocalClarityEnabled"
              @input="$emit('update:params', { ...params, vocalClarityLevel: parseFloat($event.target.value), vocalClarityEnabled: true })"
              class="cyber-slider"
            >
            <span class="control-value">{{ params.vocalClarityLevel.toFixed(1) }}</span>
          </div>
        </div>

        <div class="control-group" :class="{ disabled: !params.panEnabled }">
          <div class="label-row">
            <label class="cyber-switch">
              <input
                type="checkbox"
                :checked="params.panEnabled"
                @change="$emit('update:params', { ...params, panEnabled: $event.target.checked })"
              >
              <span class="switch-slider"></span>
            </label>
            <label class="control-label">声道偏移 (PAN)</label>
          </div>
          <div class="slider-wrap">
            <input
              type="range" min="-1" max="1" step="0.1"
              :value="params.pan"
              :disabled="!params.panEnabled"
              @input="$emit('update:params', { ...params, pan: parseFloat($event.target.value), panEnabled: true })"
              class="cyber-slider"
            >
            <span class="control-value">{{ params.pan.toFixed(1) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- AI 智能算法 (增强) -->
    <div class="control-section ai-special-section">
      <h3 class="section-title">AI智能千面处理（高阶）</h3>
      <div class="control-row">
        <div class="control-group ai-group" :class="{ disabled: !params.aiDedupEnabled }">
          <div class="label-row">
            <label class="cyber-switch ai">
              <input
                type="checkbox"
                :checked="params.aiDedupEnabled"
                @change="$emit('update:params', { ...params, aiDedupEnabled: $event.target.checked })"
              >
              <span class="switch-slider"></span>
            </label>
            <label class="control-label">AI 实时智能去重</label>
          </div>
          <div class="slider-wrap">
            <input
              type="range" min="0" max="100" step="1"
              :value="params.aiDedupIntensity"
              :disabled="!params.aiDedupEnabled"
              @input="$emit('update:params', { ...params, aiDedupIntensity: parseInt($event.target.value), aiDedupEnabled: true })"
              class="cyber-slider ai"
            >
            <span class="control-value ai">{{ Math.round(params.aiDedupIntensity) }}%</span>
          </div>
        </div>

        <div class="control-group ai-group" :class="{ disabled: !params.aiRecomposeEnabled }">
          <div class="label-row">
            <label class="cyber-switch ai">
              <input
                type="checkbox"
                :checked="params.aiRecomposeEnabled"
                @change="$emit('update:params', { ...params, aiRecomposeEnabled: $event.target.checked })"
              >
              <span class="switch-slider"></span>
            </label>
            <label class="control-label">AI 实时智能重组</label>
          </div>
          <div class="slider-wrap">
            <input
              type="range" min="0" max="100" step="1"
              :value="params.aiRecomposeIntensity"
              :disabled="!params.aiRecomposeEnabled"
              @input="$emit('update:params', { ...params, aiRecomposeIntensity: parseInt($event.target.value), aiRecomposeEnabled: true })"
              class="cyber-slider ai"
            >
            <span class="control-value ai">{{ Math.round(params.aiRecomposeIntensity) }}%</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AudioControls',
  props: {
    params: {
      type: Object,
      default: () => ({
        volume: 1,
        volumeEnabled: true,
        playbackRate: 1,
        playbackRateEnabled: false,
        pitch: 0,
        pitchEnabled: false,
        bass: 0,
        bassEnabled: false,
        compressorThreshold: -24,
        compressorEnabled: false,
        reverb: 0,
        reverbEnabled: false,
        noiseLevel: 0,
        noiseEnabled: false,
        echoLevel: 0,
        echoEnabled: false,
        vocalClarityLevel: 0,
        vocalClarityEnabled: false,
        pan: 0,
        panEnabled: false,
        aiDedupIntensity: 50,
        aiDedupEnabled: false,
        aiRecomposeIntensity: 50,
        aiRecomposeEnabled: false
      })
    }
  }
}
</script>

<style scoped>
.audio-controls {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.control-section {
  padding-bottom: 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.control-section:last-child {
  border-bottom: none;
}

.section-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--accent);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 10px;
  opacity: 0.8;
}

.control-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
  transition: opacity 0.3s ease;
}

.control-group.disabled .slider-wrap {
  opacity: 0.4;
  pointer-events: none;
}

.ai-special-section {
  position: relative;
  border-bottom: none;
  background: linear-gradient(to right, rgba(0, 212, 255, 0.08), rgba(0, 212, 255, 0.02));
  margin: 0 -20px;
  padding: 14px 20px;
  border-radius: 0 8px 8px 0;
  box-shadow: inset 10px 0 20px -10px rgba(0, 212, 255, 0.1);
  overflow: hidden;
}

.ai-special-section::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--accent);
  box-shadow: 0 0 10px var(--accent-glow);
}

.ai-special-section .section-title {
  color: #fff;
  text-shadow: 0 0 10px var(--accent-glow);
  letter-spacing: 1px;
  font-size: 16px;
  font-family: 'JetBrains Mono', 'PingFang SC', sans-serif;
  font-weight: 700;
  font-style: italic;
  opacity: 1;
}

.control-value.ai {
  color: var(--accent);
  font-weight: bold;
}

.cyber-slider.ai::-webkit-slider-thumb {
  background: #fff;
  border: 2px solid var(--accent);
  box-shadow: 0 0 10px var(--accent);
}

.cyber-switch.ai input:checked + .switch-slider {
  background-color: rgba(0, 212, 255, 0.2);
  border-color: var(--accent);
}

.cyber-switch.ai input:checked + .switch-slider::before {
  background-color: #fff;
  box-shadow: 0 0 12px var(--accent);
}

.label-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.control-label {
  font-size: 11px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cyber-switch {
  position: relative;
  display: inline-block;
  width: 32px;
  height: 18px;
}

.cyber-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.1);
  transition: 0.4s;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.switch-slider::before {
  position: absolute;
  content: '';
  height: 12px;
  width: 12px;
  left: 2px;
  bottom: 2px;
  background-color: #666;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .switch-slider {
  background-color: rgba(0, 212, 255, 0.2);
  border-color: var(--accent);
}

input:checked + .switch-slider::before {
  transform: translateX(14px);
  background-color: var(--accent);
  box-shadow: 0 0 8px var(--accent);
}

.slider-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 60%;
}

.control-value {
  font-family: 'Orbitron', sans-serif;
  font-size: 11px;
  color: var(--accent);
  width: 45px;
  text-align: right;
  flex-shrink: 0;
}

.cyber-slider {
  flex: 1;
  max-width: 80px;
  -webkit-appearance: none;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  outline: none;
}

.cyber-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 12px;
  height: 12px;
  background: var(--accent);
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 0 10px var(--accent);
  transition: all 0.2s ease;
}

.cyber-slider::-webkit-slider-thumb:hover {
  transform: scale(1.2);
  box-shadow: 0 0 15px var(--accent);
}
</style>
